import * as THREE from "three";
import {Box3, Vector3} from "three";
import * as Globals from "../../globals";
import {HBESectionWidth} from "../../globals";
import {raycaster, scene} from "../../sceneManager";
import {buildingGroup, mousePosition} from "../../main";
import {setObjectLayer} from "../../utils/layerUtils";
import {addEdgeGeometry, findChildByName, findChildrenByName} from "../../utils/threeUtils";
import {getInnerWalls, getWallElementsBoundingBoxMeshes} from "../../utils/configuratorUtils";
import {areOverlapping} from "../../utils/mathUtils";
import {getIntersectingWalls, getWallOrientation} from "../walls/wall";
import {toFixedFloat} from "../../utils/utils";
import {placeableWalls} from "./cutOutGeneration";
import {controlObjectMaterial, previewMaterial, previewNotAllowedMaterial} from "../../constants/materialConstants";
import {controlObjectName, supportBeamMeshName} from "../../constants/nameConstants";

const minimalWallEndMargin = 0.12; //m
export const minMaxPositionSnapDistance = 0.05; //m

export class CutOutPreview {
    constructor(width, height, bottomOffset) {
        this.width = width;
        this.elementWidth = this.width / Globals.HBESectionWidth;
        this.height = height;
        this.bottomOffset = bottomOffset !== null ? bottomOffset : 0;
        this.object = null;
        this.placeable = false;
        this.recreated = false;
        this.marginLeftRight = minimalWallEndMargin;
        this.snapToHBEElements = true;
        this.HBEWallBoxes = getWallElementsBoundingBoxMeshes();
        this.supportBeams = findChildrenByName(buildingGroup, supportBeamMeshName);
        this.placeableWalls = placeableWalls.both;
        this.innerWalls = getInnerWalls();
    }

    static createFromControlObject(controlObject) {
        let width = controlObject.userData.width;
        let height = controlObject.userData.height;
        let bottomOffset = controlObject.userData.bottomOffset;

        let preview = new CutOutPreview(width, height, bottomOffset);
        preview.object = controlObject;
        preview.recreated = true;
        return preview;
    }

    create() {
        let previewGeometry = new THREE.PlaneGeometry(this.width, Globals.HBEWallDefaultThickness);
        let previewMesh = new THREE.Mesh(previewGeometry, previewMaterial);
        addEdgeGeometry(previewMesh);
        previewMesh.name = controlObjectName;
        previewMesh.userData.width = this.width;
        previewMesh.userData.height = this.height;
        previewMesh.userData.bottomOffset = this.bottomOffset;

        setObjectLayer(previewMesh, Globals.layers.layoutViewOnly, true);

        previewMesh.rotateX(-Math.PI / 2);
        previewMesh.position.y = Globals.defaultPreviewHeight;

        this.object = previewMesh;
    }

    recreate() {
        this.HBEWallBoxes = getWallElementsBoundingBoxMeshes();
        let control = this.object;
        control.userData.originalParent = control.parent.uuid; //Überschreiben des parent falls in gespeicherter Szene
        control.removeFromParent();
        control.material = controlObjectMaterial;
        control.position.set(control.userData.sceneMatrix.position.x, Globals.defaultPreviewHeight, control.userData.sceneMatrix.position.z);
        control.rotation.set(control.userData.sceneMatrix.rotation.x, control.userData.sceneMatrix.rotation.y, control.userData.sceneMatrix.rotation.z);
        control.updateMatrix();
        scene.addWithUuid(control);
    }

    //Anpassung der Preview basierend auf aktuellen Parametern
    adjustPreview() {
        this.placeable = false;

        let intersect = raycaster.intersectObjects(this.HBEWallBoxes, false);
        if (intersect.length === 0) {
            this.positionAsNotPlaceable();
            return;
        }
        let wallMeshBBox = intersect[0].object;
        let wallMesh = wallMeshBBox.parent; //Von BoundingBox Element auf Wandmesh

        if (wallMesh == null) {
            this.HBEWallBoxes = getWallElementsBoundingBoxMeshes();
            let intersect = raycaster.intersectObjects(this.HBEWallBoxes, false);
            if (intersect.length === 0) {
                this.positionAsNotPlaceable();
                return;
            }
            wallMeshBBox = intersect[0].object;
            wallMesh = wallMeshBBox.parent; //Von BoundingBox Element auf Wandmesh
        }

        if (this.snapToHBEElements) {
            //Platzierung am ersten und letzten Wandelement einer Wandgruppe verhindern
            if ((wallMesh.userData.isFirstElement !== undefined && wallMesh.userData.isFirstElement) || (wallMesh.userData.isLastElement !== undefined && wallMesh.userData.isLastElement)) {
                this.positionAsNotPlaceable();
                return;
            }
        }

        let wallGroup = wallMesh.parent; //Wandgruppe auslesen
        let wallOrientation = getWallOrientation(wallGroup);
        this.setPositionOfControlObject(wallGroup, wallMeshBBox, wallOrientation);

        if (this.checkValidPosition(wallGroup, wallOrientation)) {
            this.object.material = previewMaterial;
            this.placeable = true;
            return;
        }

        this.positionAsNotPlaceable();
    }

    checkValidPosition(wallGroup, wallOrientation) {
        //Limit cutout placement to allowed wall type.
        //Zuerst ausführen. Keine lange Berechnung notwendig, wenn sowieso nicht erlaubt.
        let isOuterWall = wallGroup.userData.isOuterWall;
        if (this.placeableWalls === placeableWalls.interior && isOuterWall) {
            return false;
        } else if (this.placeableWalls === placeableWalls.exterior && !isOuterWall) {
            return false;
        }

        //Create BoundingBox for wall
        let wallBounds = new Box3().setFromObject(wallGroup);
        let wallStart = wallOrientation === "x" ? wallBounds.min.x : wallBounds.min.z;
        let wallEnd = wallOrientation === "x" ? wallBounds.max.x : wallBounds.max.z;

        let newControlBounds = new Box3().setFromObject(this.object);
        let newControlStart = wallOrientation === "x" ? newControlBounds.min.x : newControlBounds.min.z;
        let newControlEnd = wallOrientation === "x" ? newControlBounds.max.x : newControlBounds.max.z;

        let firstElement = null;
        let lastElement = null;

        for (let i = 0; i < wallGroup.children.length; i++) {
            let child = wallGroup.children[i];
            if (child.type === "Mesh") {
                if (child.userData.isFirstElement) {
                    firstElement = child;
                } else if (child.userData.isLastElement) {
                    lastElement = child;
                }
            }
        }

        if (firstElement === null || lastElement === null) {
            console.error("Wall doesnt contain a first and last element.");
        }

        let firstElementBounds = new Box3().setFromObject(findChildByName(firstElement, "wallMeshBBox", true));
        let lastElementBounds = new Box3().setFromObject(findChildByName(lastElement, "wallMeshBBox", true));
        let firstElementSize = toFixedFloat(wallOrientation === "x" ? firstElementBounds.getSize(new Vector3()).x : firstElementBounds.getSize(new Vector3()).z);
        let lastElementSize = toFixedFloat(wallOrientation === "x" ? lastElementBounds.getSize(new Vector3()).x : lastElementBounds.getSize(new Vector3()).z);


        //Ggf. erstes und letztes Element swappen um sicherzustellen, dass der Start immer an der kleineren Position liegt
        if ((wallOrientation === "x" && firstElementBounds.min.x > lastElementBounds.min.x) || (wallOrientation === "z" && firstElementBounds.min.z > lastElementBounds.min.z)) {
            let tmp = firstElementSize;
            firstElementSize = lastElementSize;
            lastElementSize = tmp;
        }

        let firstElementMargin = firstElementSize >= minimalWallEndMargin ? firstElementSize : firstElementSize + HBESectionWidth;
        let lastElementMargin = lastElementSize >= minimalWallEndMargin ? lastElementSize : lastElementSize + HBESectionWidth;

        //Check if margin to wall end is valid
        /* Bei Platzierung am letzten Element muss dieses mindestens 12 cm sein.
         * Wenn mind 12 cm kann am zweiten oder vorletzten bereits platziert werden
         * Wenn kleiner 12 cm muss ein zusätzliches Element zwischen liegen also ab 3. Elemente (von links oder rechts)
         * */

        if (!(toFixedFloat(newControlStart - firstElementMargin) >= toFixedFloat(wallStart) &&
            toFixedFloat(newControlEnd + lastElementMargin) <= toFixedFloat(wallEnd))) {
            return false;
        }

        //Check positioning of control object against existing controlObjects
        let controlObjects = findChildrenByName(wallGroup, controlObjectName, false, true);
        for (let i = 0; i < controlObjects.length; i++) {
            let controlObject = controlObjects[i];
            let controlObjectMargin = controlObject.userData.marginLeftRight; //TODO Eventuell in Float parsen
            let biggestMargin = controlObjectMargin > this.marginLeftRight ? controlObjectMargin : this.marginLeftRight; //Margin zwischen Elementen die eingehalten werden muss

            let controlBounds = new Box3().setFromObject(controlObject);
            let controlStart = wallOrientation === "x" ? controlBounds.min.x - biggestMargin : controlBounds.min.z - biggestMargin;
            let controlEnd = wallOrientation === "x" ? controlBounds.max.x + biggestMargin : controlBounds.max.z + biggestMargin;

            let overlap = areOverlapping({start: controlStart, finish: controlEnd}, {
                start: newControlStart,
                finish: newControlEnd
            });
            if (overlap) {
                //If any element gets overlapped > controlObject not placeable
                return false;
            }
        }

        //Check if cut out is too close to an existing inner wall
        let intersectingWalls = getIntersectingWalls(wallGroup, this.innerWalls, false);
        this.wallOccupiedRanges = [];
        for (let i = 0; i < intersectingWalls.length; i++) {
            let intersectingWall = intersectingWalls[i];
            let intersectingWallBounds = new Box3().setFromObject(intersectingWall);
            if (wallOrientation === "x") {
                this.wallOccupiedRanges.push({
                    start: intersectingWallBounds.min.x,
                    finish: intersectingWallBounds.max.x
                });
            } else {
                this.wallOccupiedRanges.push({
                    start: intersectingWallBounds.min.z,
                    finish: intersectingWallBounds.max.z
                });
            }
        }

        let controlBounds = new Box3().setFromObject(this.object);
        this.controlRange = null;
        if (wallOrientation === "x") {
            this.controlRange = {
                start: controlBounds.min.x,
                finish: controlBounds.max.x
            };
        } else {
            this.controlRange = {
                start: controlBounds.min.z,
                finish: controlBounds.max.z
            };
        }

        for (let i = 0; i < this.wallOccupiedRanges.length; i++) {
            if (areOverlapping(this.controlRange, this.wallOccupiedRanges[i])) {
                return false;
            }
        }

        if (this.checkForSupportBeamOverlap(wallOrientation)) {
            return false;
        }

        return true;
    }

    checkForSupportBeamOverlap(wallOrientation) {
        let previewBounds = new Box3().setFromObject(this.object);
        previewBounds.min.y = 0;
        previewBounds.max.y = 1;

        for (let i = 0; i < this.supportBeams.length; i++) {
            // A beam running along the wall rests on its Rähm over the whole length. Only a crossing beam needs a solid wall below.
            if (getWallOrientation(this.supportBeams[i]) === wallOrientation) {
                continue;
            }
            let supportBeamBounds = new Box3().setFromObject(this.supportBeams[i]);
            supportBeamBounds.min.y = 0;
            supportBeamBounds.max.y = 1;

            if (!supportBeamBounds.intersect(previewBounds).isEmpty()) {
                return true;
            }
        }

        return false;
    }

    setPositionOfControlObject(wallGroup, wallMeshBBox, wallOrientation) {
        //Positionierung des Previewobjekts
        let originalYPosition = this.object.position.y;
        this.object.position.set(0, 0, 0); //Nullen der Position da diese sonst auf Matrix addiert wird
        this.object.rotation.set(-Math.PI / 2, 0, wallOrientation === "z" ? Math.PI / 2 : 0);

        let wallMeshBBoxGlobalPosition = wallMeshBBox.getWorldPosition(new Vector3());
        this.object.position.copy(wallMeshBBoxGlobalPosition);
        if (this.snapToHBEElements) {
            if (this.elementWidth % 2 === 0) {
                if (wallOrientation === "x") {
                    let offset = mousePosition.x > wallMeshBBoxGlobalPosition.x ? Globals.HBESectionWidth / 2 : -Globals.HBESectionWidth / 2;
                    this.object.position.x += offset;
                } else {
                    let offset = mousePosition.z > wallMeshBBoxGlobalPosition.z ? Globals.HBESectionWidth / 2 : -Globals.HBESectionWidth / 2;
                    this.object.position.z += offset;
                }
            }
        } else {
            let bounds = new Box3().setFromObject(wallGroup);
            if (wallOrientation === "x") {
                this.object.position.x = mousePosition.x;
                let minSnapPoint = toFixedFloat(bounds.min.x + this.marginLeftRight + (this.width / 2));
                let maxSnapPoint = toFixedFloat(bounds.max.x - this.marginLeftRight - (this.width / 2));
                if (this.object.position.x <= minSnapPoint + minMaxPositionSnapDistance) {
                    this.object.position.x = minSnapPoint;
                }
                if (this.object.position.x >= maxSnapPoint - minMaxPositionSnapDistance) {
                    this.object.position.x = maxSnapPoint;
                }
            } else {
                this.object.position.z = mousePosition.z;
                let minSnapPoint = toFixedFloat(bounds.min.z + this.marginLeftRight + (this.width / 2));
                let maxSnapPoint = toFixedFloat(bounds.max.z - this.marginLeftRight - (this.width / 2));
                if (this.object.position.z <= minSnapPoint + minMaxPositionSnapDistance) {
                    this.object.position.z = minSnapPoint;
                }
                if (this.object.position.z >= maxSnapPoint - minMaxPositionSnapDistance) {
                    this.object.position.z = maxSnapPoint;
                }
            }
        }

        this.object.position.setY(originalYPosition);
    }

    positionAsNotPlaceable() {
        this.placeable = false;
        this.object.material = previewNotAllowedMaterial;
        this.setToMousePosition(mousePosition);
    }

    setToMousePosition(position) {
        this.object.position.set(position.x, this.object.position.y, position.z);
    }
}
